/**
 * Tikron connection wrapper for the online 1v1 mode.
 *
 * Thin seam over `@tikron/client`: it owns the per-tab session key, room-code
 * generation, the three connect flows (host / join-by-code / random), and
 * fan-out of the two server streams the game cares about — authoritative
 * `MatchState` (via `onStateChange`) and `evt` FX messages. The client sends
 * only intents through `send()`; it never asserts outcomes.
 */

import { GameClient, RoomJoinError, type Room } from "@tikron/client";
import type { MatchState, SimEvent } from "./types.js";

/** Party = kebab-case of the DO binding `ArenaRoom`. Keep in sync with wrangler.jsonc. */
const PARTY = "arena-room";

/** Room-code alphabet: A–Z + 2–9 minus lookalikes (I, L, O). */
export const ROOM_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LEN = 5;

/** Number of shared "quick" rooms probed for random matching. */
const QUICK_ROOMS = 5;
/**
 * Safety timeout around the `joinOrCreate` CALL itself. `@tikron/client` 0.2.1
 * already rejects with `RoomJoinError` on the expected failure paths
 * (room_full, a pre-Welcome close/error) — this only guards a connection that
 * never settles either way (e.g. a wedged network).
 */
const JOIN_TIMEOUT_MS = 10_000;

export interface NetHandlers {
  onState(state: MatchState): void;
  onEvt(ev: SimEvent): void;
  onStatus(msg: string): void;
  /** Phase 3: a relayed WebRTC signaling message from the opponent (opaque payload). */
  onRtc?(payload: unknown): void;
}

/** Generate a fresh, unambiguous room code (e.g. "K7QW3"). */
export function makeRoomCode(): string {
  const buf = new Uint32Array(ROOM_CODE_LEN);
  crypto.getRandomValues(buf);
  let s = "";
  for (let i = 0; i < ROOM_CODE_LEN; i++) {
    s += ROOM_ALPHABET[(buf[i] ?? 0) % ROOM_ALPHABET.length];
  }
  return s;
}

/** Normalize user input into a canonical code, or null if it can't be one. */
export function normalizeRoomCode(raw: string): string | null {
  const up = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (up.length !== ROOM_CODE_LEN) return null;
  for (const ch of up) if (!ROOM_ALPHABET.includes(ch)) return null;
  return up;
}

export function roomNameForCode(code: string): string {
  return `r-${code}`;
}

const SESSION_STORAGE_KEY = "nyam-session";

/** Per-tab stable session key so a reconnect reclaims the same seat. */
function sessionKey(): string {
  let s = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!s) {
    s =
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `s-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    sessionStorage.setItem(SESSION_STORAGE_KEY, s);
  }
  return s;
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error("join-timeout")), ms);
    p.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

/** Map a failed join into a user-facing Korean message. */
function joinErrorMessage(err: unknown): string {
  if (err instanceof RoomJoinError) {
    if (err.code === "room_full") return "방이 꽉 찼어요";
    return "연결에 실패했어요";
  }
  if (err instanceof Error && err.message === "join-timeout") {
    return "연결 시간이 초과됐어요. 다시 시도해 주세요";
  }
  return "연결에 실패했어요";
}

export class NetClient {
  private readonly client: GameClient;
  private readonly session = sessionKey();
  private room: Room | null = null;

  /** Stable id the room knows us by (== our session key). Set on connect. */
  myId = "";
  /** Wall-clock time of the last authoritative state, for render extrapolation. */
  lastStateAt = 0;
  /** Latest authoritative state (also delivered via handlers.onState). */
  latest: MatchState | null = null;

  constructor() {
    this.client = new GameClient(location.host, { party: PARTY });
  }

  /** Host a fresh room; resolves to the shareable room code. */
  async host(handlers: NetHandlers): Promise<string> {
    const code = makeRoomCode();
    try {
      await this.join(roomNameForCode(code), handlers);
    } catch (err) {
      throw new Error(joinErrorMessage(err));
    }
    return code;
  }

  /** Join an existing room by its code. */
  async joinCode(code: string, handlers: NetHandlers): Promise<string> {
    try {
      await this.join(roomNameForCode(code), handlers);
    } catch (err) {
      throw new Error(joinErrorMessage(err));
    }
    return code;
  }

  /**
   * Random matching. `client.matchmake()` needs a deployed matchmaker
   * (`/api/matchmake`) that this standalone worker does not run, so we probe the
   * shared quick rooms and take the first that accepts us — with `maxClients = 2`
   * the server rejects (room_full) any room already holding two players, so the
   * first room we successfully connect to always ends up with ≤ 2 players.
   * `joinOrCreate` now rejects (RoomJoinError) instead of hanging when a room
   * is full or the connection drops pre-Welcome; either failure — or our own
   * join timeout — just means "try the next quick room".
   */
  async randomMatch(handlers: NetHandlers): Promise<string> {
    for (let i = 1; i <= QUICK_ROOMS; i++) {
      const name = `quick-${i}`;
      handlers.onStatus(`빈 방 찾는 중… (${i}/${QUICK_ROOMS})`);
      try {
        await this.join(name, handlers);
        return name;
      } catch {
        // Nothing to tear down here: a rejected joinOrCreate never handed us a
        // Room (the SDK's own teardown already closed the socket) — just move on.
      }
    }
    throw new Error("빈 방이 없어요. 잠시 후 다시 시도해 주세요");
  }

  private async join(name: string, handlers: NetHandlers): Promise<void> {
    const room = await withTimeout(this.client.joinOrCreate(name, { _session: this.session }), JOIN_TIMEOUT_MS);
    this.wire(room, handlers);
    const welcome = await room.connected();
    this.room = room;
    this.myId = welcome.connectionId || this.session;
  }

  /** Subscribe to state + evt BEFORE connect resolves so no early frame is lost. */
  private wire(room: Room, handlers: NetHandlers): void {
    room.onStateChange((s) => {
      const state = s as MatchState;
      this.latest = state;
      this.lastStateAt = performance.now();
      handlers.onState(state);
    });
    room.onMessage("evt", (payload) => {
      handlers.onEvt(payload as SimEvent);
    });
    room.onMessage("rtc", (payload) => {
      handlers.onRtc?.(payload);
    });
  }

  send(type: string, payload?: unknown): void {
    this.room?.send(type, payload);
  }

  /** Ms since the last authoritative state — the food-fall extrapolation window. */
  extrapolationMs(nowMs: number): number {
    if (this.lastStateAt <= 0) return 0;
    return Math.max(0, Math.min(nowMs - this.lastStateAt, 250));
  }

  leave(): void {
    try {
      this.room?.leave();
    } catch {
      /* ignore */
    }
    this.room = null;
  }
}
